import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PortalShell from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocUploader } from "@/components/portal/DocUploader";
import { FastDocs } from "@/components/portal/FastDocs";
import { DirectorPick, roleFor, tidyName } from "@/components/portal/lookup/DirectorPick";
import { WelcomeBack } from "@/components/portal/WelcomeBack";
import { forgetMe, recallMe, rememberMe } from "@/lib/returning";
import { otherFiles, sendFile } from "@/lib/fastDocs";
import { CompanyLookup } from "@/components/portal/lookup/CompanyLookup";
import { useStepTop } from "@/lib/useStepTop";
import { StepBar, useStepCheer } from "@/components/portal/StepCheer";
import { FinishLine } from "@/components/portal/FinishLine";
import { fillEmpty } from "@/lib/lookups";
import { MoneyField, CountField, PhoneField } from "@/components/ui/number-fields";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, ShieldCheck, Building2, Banknote, FileText, Lock } from "lucide-react";
import { COUNTRIES } from "@/pages/hub/constants";

const STEPS = ["Your business", "You", "The funding", "Documents"];
const CHEERS = ["Business sorted", "Your details are in", "Funding noted · last step"];
const URGENCY = [
  { value: "urgent", label: "As soon as possible" },
  { value: "this_week", label: "Within a week" },
  { value: "this_month", label: "Within a month" },
  { value: "flexible", label: "Just exploring" },
];

const money = (n) => `£${Math.round(Number(n) || 0).toLocaleString()}`;

export default function PublicApply() {
  const { slug, token } = useParams();
  const [params] = useSearchParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    applicant_name: "", applicant_email: "", applicant_phone: "",
    business_name: "", company_number: "", industry_sector: "", trading_years: "", country: "",
    annual_revenue_gbp: "", employees: "", company_address: "", incorporation_date: "", directors: [],
    finance_type: "", amount_gbp: "", term_months: "", loan_purpose: "", urgency: "",
    security_offered: "", existing_debt_summary: "", notes: "", files: [],
  });
  const [consent, setConsent] = useState(false);
  const [trap, setTrap] = useState("");
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [me, setMe] = useState(() => recallMe());
  const startedAt = useRef(Date.now());
  const saveTimer = useRef(null);
  const topRef = useStepTop(step);
  const cheer = useStepCheer(step, CHEERS);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const onText = (k) => (e) => set(k)(e.target.value);
  // The ad click that sent them, minted at /c/<code>. Kept in session storage so a refresh,
  // or a hop through the explainer page, doesn't lose the attribution.
  const ck = useMemo(() => {
    const fromUrl = (params.get("ck") || "").trim();
    try {
      if (fromUrl) sessionStorage.setItem("cx_ck", fromUrl);
      return fromUrl || sessionStorage.getItem("cx_ck") || null;
    } catch { return fromUrl || null; }
  }, [params]);

  // ── resolve the link ──────────────────────────────────────
  useEffect(() => {
    if (slug) {
      // remembered so /business-funding can offer "pick your application back up"
      try { localStorage.setItem("cx_apply_slug", slug); } catch { /* private browsing */ }
    }
    const url = token ? `/apply/invite/${token}` : `/apply/link/${slug}`;
    api.get(url)
      .then((r) => {
        const d = r.data;
        setCtx(d);
        if (d.session) setSession(d.session);
        // the funding page's "which route fits" answers arrive as ?type=&amount=
        const wantType = (params.get("type") || "").trim();
        const wantAmount = (params.get("amount") || "").replace(/[^\d]/g, "");
        const okType = wantType && (d.finance_types || []).includes(wantType) ? wantType : "";
        setF((s) => ({
          ...s,
          ...(d.draft || {}),
          applicant_name: d.draft?.applicant_name || d.customer?.name || "",
          applicant_email: d.draft?.applicant_email || d.customer?.email || "",
          applicant_phone: d.draft?.applicant_phone || d.customer?.phone || "",
          finance_type: d.draft?.finance_type || d.prefill?.finance_type || okType || "",
          amount_gbp: d.draft?.amount_gbp || d.prefill?.amount_gbp || wantAmount || "",
        }));
        if (d.step) setStep(Math.min(d.step, STEPS.length - 1));
      })
      .catch((e) => setErr(e?.response?.data?.detail || "This application link isn't valid any more."));
  }, [slug, token]);

  // ── autosave (debounced) so nothing is ever lost ──────────
  useEffect(() => {
    if (!ctx || done) return;
    if (!f.applicant_name && !f.applicant_email && !f.business_name) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post("/apply/session", { slug, token, session, ck, draft: f, step })
        .then((r) => { if (r.data?.session && !session) setSession(r.data.session); })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [f, step, ctx, done]);  // eslint-disable-line

  const stepValid = useMemo(() => {
    if (step === 0) return f.business_name.trim().length > 1;
    if (step === 1) return f.applicant_name.trim().length > 1 && /\S+@\S+\.\S+/.test(f.applicant_email) && f.applicant_phone.trim().length > 6;
    if (step === 2) return !!f.finance_type && Number(f.amount_gbp) > 0;
    return true;
  }, [step, f]);

  // returning on the same device: one tap restores business + contact details and skips to the funding step
  const restoreMe = () => {
    const { directors, saved_at, ...vals } = me;  // eslint-disable-line no-unused-vars
    const next = { ...fillEmpty(f, vals).next, directors: directors || [] };
    setF(next);
    setMe(null);
    const ready = next.business_name.trim().length > 1 && next.applicant_name.trim().length > 1
      && /\S+@\S+\.\S+/.test(next.applicant_email) && next.applicant_phone.trim().length > 6;
    if (ready) setStep(2);
    toast.success(ready ? "Welcome back — your business and contact details are in. Just tell us about the funding."
      : "Welcome back — we've put your details back in. Check them and carry on.");
  };
  const showWelcome = !!me && step === 0 && !done && !f.business_name && !f.applicant_email;

  const submit = async () => {
    if (!consent) { toast.error("Please tick the consent box so we can approach lenders for you"); return; }
    setBusy(true);
    try {
      const payload = {
        slug, token, session, consent, trap, ck,
        elapsed_ms: Date.now() - startedAt.current,
        applicant_name: f.applicant_name.trim(),
        applicant_position: roleFor((f.directors || []).find((d) => tidyName(d.name) === f.applicant_name.trim())?.role) || null,
        applicant_email: f.applicant_email.trim(),
        applicant_phone: f.applicant_phone.trim(),
        business_name: f.business_name.trim(),
        company_number: f.company_number.trim() || null,
        company_address: f.company_address || null,
        incorporation_date: f.incorporation_date || null,
        industry_sector: f.industry_sector || null,
        country: f.country || null,
        trading_years: f.trading_years === "" ? null : Number(f.trading_years),
        annual_revenue_gbp: f.annual_revenue_gbp === "" ? null : Number(f.annual_revenue_gbp),
        employees: f.employees === "" ? null : Number(f.employees),
        finance_type: f.finance_type,
        amount_gbp: Number(f.amount_gbp),
        term_months: f.term_months === "" ? null : Number(f.term_months),
        loan_purpose: f.loan_purpose || null,
        urgency: f.urgency || null,
        security_offered: f.security_offered || null,
        existing_debt_summary: f.existing_debt_summary || null,
        notes: f.notes || null,
        file_ids: (f.files || []).map((x) => x.id),
      };
      const r = await api.post("/apply/submit", payload);
      rememberMe(f);
      setDone(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't submit that — please check your details and try again");
    } finally { setBusy(false); }
  };

  const intro = ctx?.introducer?.name;
  const direct = !!ctx?.introducer?.house;
  const helpCta = (
    <div className="flex items-center gap-2">
      <a href="/business-funding" className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center text-white transition-colors" data-testid="apply-about-connexis">
        Who are Connexis?
      </a>
      <a href="mailto:ops@connex.is" className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center text-white transition-colors" data-testid="apply-help">
        Need a hand?
      </a>
    </div>
  );

  // ── states ────────────────────────────────────────────────
  if (err) {
    return (
      <PortalShell variant="apply" kind="introducer" eyebrow="Connexis · Business funding"
                   title="This link has expired" subtitle={err} ctas={helpCta}>
        <div className="mx-auto max-w-lg rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="apply-error">
          <p className="text-[13.5px] text-slate-300 leading-relaxed">
            Ask whoever sent it for a fresh link, or email{" "}
            <a href="mailto:ops@connex.is" className="text-[#7da9ff] underline underline-offset-2">ops@connex.is</a> and we'll
            get you a new one straight away.
          </p>
        </div>
      </PortalShell>
    );
  }

  if (!ctx) {
    return (
      <div className="min-h-screen bg-[#0B1220] grid place-items-center">
        <Loader2 className="size-6 animate-spin text-[#7da9ff]" />
      </div>
    );
  }

  if (done) {
    return (
      <PortalShell variant="apply" kind="introducer" eyebrow="Connexis · Application received"
                   title="That's it — we've got everything we need"
                   ctas={helpCta}
                   subtitle="Our credit desk is on it. You'll hear from us within one working day.">
        <div className="mx-auto max-w-xl" data-testid="apply-success">
          <FinishLine reference={done.reference} copied={refCopied}
                      onCopy={() => { navigator.clipboard?.writeText(done.reference); setRefCopied(true); setTimeout(() => setRefCopied(false), 2000); }}
                      steps={[
                        { title: "Application received", sub: `Just now — a copy of your reference is on its way to ${f.applicant_email}.` },
                        { title: "Matched with the right lenders", sub: "We pick the lenders most likely to say yes — usually within one working day." },
                        direct
                          ? { title: "A named contact gets in touch", sub: "From the Connexis funding desk, to talk you through the options." }
                          : { title: "A named contact gets in touch", sub: `${intro} has been notified and can chase it with us on your behalf.` },
                      ]}>
            {done.status_token && (
              <a href={`/apply/status/${done.status_token}`}
                 className="mt-6 inline-flex items-center justify-center h-11 w-full rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[14px] font-semibold transition-colors"
                 data-testid="apply-track-link">
                Track your application
              </a>
            )}
          </FinishLine>
          <p className="mt-5 text-center text-[12px] text-slate-500">
            Connexis Group{direct ? "" : ` · Introduced by ${intro}`}. This is not a credit agreement or an offer of finance.
          </p>
          <RememberedNote />
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell variant="apply" kind="introducer" eyebrow="Connexis · Business funding"
                 title="Apply for business funding"
                 ctas={helpCta}
                 subtitle={direct
                   ? "Three minutes, no obligation, and it saves as you go. Straight to the Connexis funding desk."
                   : `Three minutes, no obligation, and it saves as you go. Introduced by ${intro}.`}>
      <div className="mx-auto max-w-2xl" data-testid="public-apply">
        <StepBar steps={STEPS} step={step} barRef={topRef} cheer={cheer} />

        <div className="rounded-3xl border border-white/10 bg-white text-slate-900 p-5 sm:p-7">
          {/* honeypot — hidden from humans */}
          <input tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)}
                 name="company_website_confirm" aria-hidden="true"
                 style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

          {step === 0 && (
            <div className="space-y-4" data-testid="apply-step-business">
              {showWelcome && <WelcomeBack me={me} onUse={restoreMe} onForget={() => { forgetMe(); setMe(null); }} />}
              <Head Icon={Building2} title="First, find the business" sub="Search for it and we'll fill in what we can — or just type the basics." />
              <CompanyLookup testId="apply-company-lookup" onPick={(c) => {
                const vals = {
                  business_name: c.business_name, company_number: c.company_number, trading_years: c.trading_years,
                  industry_sector: c.industry_sector, annual_revenue_gbp: c.annual_revenue_gbp, employees: c.employees,
                  country: "United Kingdom",  // every Companies House company is UK-registered
                };
                // not on screen, but the desk and the lenders get them — and the directors offer themselves on the next step
                const hidden = { company_address: c.company_address || "", incorporation_date: c.incorporation_date || "", directors: c.directors || [] };
                setF((s) => ({ ...fillEmpty(s, vals).next, ...hidden }));
                return fillEmpty(f, vals).filled;
              }} />
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Business name">
                  <Input value={f.business_name} onChange={onText("business_name")} placeholder="Smith Trading Ltd" data-testid="apply-business" />
                </Field>
                <Field label="Companies House number" hint="optional">
                  <Input value={f.company_number} onChange={onText("company_number")} placeholder="12345678" data-testid="apply-company-number" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Industry" hint="optional">
                  <Input value={f.industry_sector} onChange={onText("industry_sector")} placeholder="Construction" data-testid="apply-industry" />
                </Field>
                <Field label="Years trading" hint="optional">
                  <CountField value={f.trading_years} onChange={set("trading_years")} min={0} max={100} suffix="yrs" testId="apply-years" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Annual turnover" hint="optional">
                  <MoneyField value={f.annual_revenue_gbp} onChange={set("annual_revenue_gbp")} ladder="revenue" testId="apply-revenue" />
                </Field>
                <Field label="Employees" hint="optional">
                  <CountField value={f.employees} onChange={set("employees")} min={0} max={100000} testId="apply-employees" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Country" hint="where the business operates">
                  <Select value={f.country || undefined} onValueChange={set("country")}>
                    <SelectTrigger data-testid="apply-country"><SelectValue placeholder="Select a country…" /></SelectTrigger>
                    <SelectContent className="max-h-56">
                      {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="apply-step-you">
              <Head Icon={ShieldCheck} title="Now, who are you?" sub="So we know who to come back to." />
              <DirectorPick directors={f.directors} value={f.applicant_name} testId="apply-director"
                            onPick={(name) => setF((s) => ({ ...s, applicant_name: name }))} />
              <Field label="Your full name">
                <Input value={f.applicant_name} onChange={onText("applicant_name")} placeholder="Jane Smith" data-testid="apply-name" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Email">
                  <Input type="email" value={f.applicant_email} onChange={onText("applicant_email")} placeholder="jane@company.co.uk" data-testid="apply-email" />
                </Field>
                <Field label="Mobile">
                  <PhoneField value={f.applicant_phone} onChange={set("applicant_phone")} testId="apply-phone"
                              emptyHint="So we can call you back about this application" />
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="apply-step-funding">
              <Head Icon={Banknote} title="What are you looking for?" sub="Rough numbers are fine — nothing is committed." />
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Type of finance">
                  <Select value={f.finance_type} onValueChange={set("finance_type")}>
                    <SelectTrigger data-testid="apply-finance-type"><SelectValue placeholder="Choose one" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(ctx.finance_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="How much?">
                  <MoneyField value={f.amount_gbp} onChange={set("amount_gbp")} ladder="deal" testId="apply-amount" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Term" hint="optional">
                  <CountField value={f.term_months} onChange={set("term_months")} min={0} max={480} suffix="months" testId="apply-term" />
                </Field>
                <Field label="How soon?" hint="optional">
                  <Select value={f.urgency} onValueChange={set("urgency")}>
                    <SelectTrigger data-testid="apply-urgency"><SelectValue placeholder="Choose one" /></SelectTrigger>
                    <SelectContent>{URGENCY.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="What's it for?" hint="optional">
                <Textarea rows={2} value={f.loan_purpose} onChange={onText("loan_purpose")}
                          placeholder="New plant machinery to take on a larger contract" data-testid="apply-purpose" />
              </Field>
              <Field label="Any existing borrowing?" hint="optional">
                <Input value={f.existing_debt_summary} onChange={onText("existing_debt_summary")}
                       placeholder="£40k asset finance with Lombard, 18 months left" data-testid="apply-existing-debt" />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="apply-step-docs">
              <Head Icon={FileText} title="Anything to upload?" sub="Optional — but it's the quickest way to a decision." />
              <FastDocs financeType={f.finance_type} files={f.files} testId="apply-fast-docs"
                        onUpload={async (label, file, onProgress) => {
                          const rec = await sendFile("/apply/upload", file, { session, label }, onProgress);
                          setF((s) => ({ ...s, files: [...s.files, rec] }));
                        }}
                        onRemove={(id) => setF((s) => ({ ...s, files: s.files.filter((x) => x.id !== id) }))} />
              <Field label="Anything else?" hint="optional">
                <DocUploader files={otherFiles(f.finance_type, f.files).others} testId="apply-docs"
                             onChange={(fs) => setF((s) => ({ ...s, files: otherFiles(s.finance_type, s.files).keep(fs) }))}
                             endpoint={session ? `/apply/upload?session=${session}` : "/apply/upload"} />
              </Field>
              <Field label="Anything else we should know?" hint="optional">
                <Textarea rows={3} value={f.notes} onChange={onText("notes")}
                          placeholder="We've had a CCJ that was settled in 2024, and a big contract starting in March" data-testid="apply-notes" />
              </Field>

              <label className="flex items-start gap-2.5 rounded-2xl border border-slate-200 bg-slate-50 p-3.5 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                       className="mt-0.5 size-4 accent-[#0055FF]" data-testid="apply-consent" />
                <span className="text-[12.5px] text-slate-600 leading-relaxed">
                  I'm happy for Connexis Group to hold these details and share them with suitable lenders and funders to
                  arrange finance for my business. I've read the{" "}
                  <Link to="/privacy" target="_blank" className="text-[#0055FF] underline underline-offset-2">privacy notice</Link>.
                </span>
              </label>
            </div>
          )}

          {/* Nav */}
          <div className="mt-6 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} className="rounded-full h-11" data-testid="apply-back">
                <ArrowLeft className="size-4 mr-1.5" /> Back
              </Button>
            ) : <span className="text-[11.5px] text-slate-400 flex items-center gap-1.5"><Lock className="size-3" /> Saved as you type</span>}

            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!stepValid}
                      className="rounded-full h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] font-semibold" data-testid="apply-next">
                Continue <ArrowRight className="size-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={busy || !consent}
                      className="rounded-full h-11 px-6 bg-emerald-500 hover:bg-emerald-600 font-semibold" data-testid="apply-submit">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <>Submit application <ArrowRight className="size-4 ml-1.5" /></>}
              </Button>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-[11.5px] text-slate-500">
          {f.amount_gbp ? `${money(f.amount_gbp)} · ` : ""}
          {direct ? "Submitted direct to Connexis Group" : `Introduced by ${intro} · arranged by Connexis Group`}.
          No personal credit checks are run by completing this form.
        </p>
      </div>
    </PortalShell>
  );
}

function Head({ Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3 pb-1">
      <span className="grid place-items-center size-9 rounded-xl border border-[#0055FF]/20 bg-[#0055FF]/[0.06] text-[#0055FF] shrink-0">
        <Icon className="size-4" />
      </span>
      <div>
        <div className="text-[16px] font-semibold text-slate-900 tracking-[-0.01em]">{title}</div>
        <div className="text-[12.5px] text-slate-500">{sub}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">
        {label}{hint && <span className="ml-1.5 normal-case tracking-normal text-slate-400 font-normal">({hint})</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


/** After applying: we remember them on this device for next time, with a one-tap way to forget. */
function RememberedNote() {
  const [gone, setGone] = useState(false);
  return (
    <p className="mt-2 text-center text-[12px] text-slate-500" data-testid="apply-remembered">
      {gone ? "Done — your details are no longer saved on this device."
        : <>We'll remember your details on this device so next time is quicker.{" "}
            <button type="button" onClick={() => { forgetMe(); setGone(true); }} data-testid="apply-forget-me"
                    className="font-semibold text-slate-300 hover:text-white underline underline-offset-2">Forget them</button></>}
    </p>
  );
}
